import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getRealtimeChannel,
  getRealtimeChannelStream,
  getRealtimeCommunityStatsAndTimeline,
  getRealtimeLanguage,
  getRealtimeLiveList,
  getRealtimeOverviewMetricsStatsAndTimeline,
  getRealtimeRisingKeyword,
  getRealtimeSteamStatsAndTimeline,
} from '@op/services/realtime';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  dealWithListData,
  dealWithListObjData,
  dealWithOldTimeline,
  dealWithTimelineData,
  getTimeArray,
  sortDataList,
} from '../utils';
import { DBO_REQ } from '@op/utils/format';
import {
  arrayToObj,
  getRationPercentValue,
  getTimeSpaceArray,
  sortTimeListFunc,
} from '@op/utils';
import { dateTypeMap } from '@op/constants';
import { getTimeline } from '@op/pages/gamestore/utils';
import { useHistory } from 'umi';
import { objToUrl } from '@op/utils/url';
import { formatDateFunc, getFilterDateQOQText } from '@op/utils/time';
import moment from 'moment';
import { goStatsDetailAction } from '@op/pages/stats/Detail';
import { sentiment_frontier } from '@op/components/Snippets/constants';
import { dealwithSteamSelectedData } from '@op/utils/data';
import { useUpdateEffect } from 'ahooks';
import { AppContext } from '../pages/KeyMetricsOverview/context';
import DBO_FWC from '@op/hooks/useFrontWebConfig';
import _ from 'lodash-es';

export function useRealtimeLanguage({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => setRecords({}),
  });
  const { refresh, refreshTime } = useContext(AppContext);

  function requestChart(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeLanguage(filter, cancelFunc, refreshTime)
          .then((data: any) => {
            const {
              timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              list,
              link_of_end_time,
              link_of_start_time,
            } = data || {};
            const { metricsKey } = extraData;
            const { topList, colorList } = dealWithListData(data, metricsKey);
            const chartData = dealWithTimelineData({
              timeline_of_start_time,
              timeline_of_end_time,
              timeline: timeline_list,
              filter,
              colorList,
              metricsKey: metricsKey.map((val) => val.value),
            });

            setRecords({
              chartData,
              topList,
              tableData: timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              link_of_end_time,
              link_of_start_time,
            });
          })
          .catch(() => {
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    requestChart();
  }, [filter]);

  useUpdateEffect(() => {
    requestChart(true);
  }, [refresh]);

  return { data: records, loading, error, refetch: requestChart };
}

export function useRealtimeChannel({ filter, extraData, isStream }: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => setRecords({}),
  });
  const { refresh, refreshTime } = useContext(AppContext);
  function requestChart(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeChannel(filter, cancelFunc, refreshTime)
          .then((data: any) => {
            const {
              timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              list,
              link_of_end_time,
              link_of_start_time,
            } = data || {};
            const { metricsKey } = extraData;
            const { topList, colorList } = dealWithListData(data, metricsKey);
            const chartData = dealWithTimelineData({
              timeline_of_start_time,
              timeline_of_end_time,
              timeline: timeline_list,
              filter,
              colorList,
              metricsKey: metricsKey.map((val) => val.value),
            });

            setRecords({
              chartData,
              topList,
              tableData: timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              link_of_end_time,
              link_of_start_time,
            });
          })
          .catch(() => {
            setError(true);
          }),
      noLoading,
    );
  }

  function requestStreamChart(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeChannelStream(filter, cancelFunc, refreshTime)
          .then((data: any) => {
            const {
              timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              list,
              link_of_end_time,
              link_of_start_time,
            } = data || {};
            const { metricsKey } = extraData;
            const { topList, colorList } = dealWithListData(data, metricsKey);
            const chartData = dealWithTimelineData({
              timeline_of_start_time,
              timeline_of_end_time,
              timeline: timeline_list,
              filter,
              colorList,
              metricsKey: metricsKey.map((val) => val.value),
            });

            setRecords({
              chartData,
              topList,
              tableData: timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              link_of_end_time,
              link_of_start_time,
            });
          })
          .catch(() => {
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    if (isStream) {
      requestStreamChart();
    } else {
      requestChart();
    }
  }, [filter, isStream]);

  useUpdateEffect(() => {
    if (isStream) {
      requestStreamChart(true);
    } else {
      requestChart(true);
    }
  }, [refresh, isStream]);

  return {
    data: records,
    loading,
    error,
    refetch: isStream ? requestStreamChart : requestChart,
  };
}

export function useSteamingData({ filter }: { filter: any }) {
  const [data, setData] = useState<any[]>([]);
  const { loading, wrapperRequestTile } = useCancelRequestHook();
  const { refresh, refreshTime } = useContext(AppContext);
  function requestLiveList(noLoading = false) {
    setData([]);

    wrapperRequestTile(
      getRealtimeLiveList,
      filter,
      noLoading,
      refreshTime,
    ).then((data) => {
      const { list } = data || {};
      if (list && Array.isArray(list)) {
        setData(list);
      }
    });
  }

  useEffect(() => {
    if (filter.edition_unified_id) {
      requestLiveList();
    }
  }, [filter]);

  useUpdateEffect(() => {
    requestLiveList(true);
  }, [refresh]);

  return {
    data,
    loading,
  };
}

export function useRealtimeOverviewMetricsStats({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [chartData, setChartData] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => {
      // setRecords({});
      setChartData({});
    },
  });
  const { refresh, refreshTime } = useContext(AppContext);
  function requestStats(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeOverviewMetricsStatsAndTimeline(
          filter,
          cancelFunc,
          refreshTime,
        )
          .then((data: any) => {
            const { metricsKey } = extraData;
            const { stats, timeline } = data;
            const topList = dealWithListObjData(stats, metricsKey);
            setChartData(timeline);
            setRecords({
              topList,
              ...stats,
            });
          })
          .catch((err: any) => {
            // setRecords({});
            // setChartData({});
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    requestStats();
  }, [filter]);

  useUpdateEffect(() => {
    if (refreshTime > 1) {
      requestStats(true);
    }
  }, [refresh]);

  return { data: records, chartData, loading, error, refetch: requestStats };
}

export function useRealtimeOverviewMetricsChart({
  filter,
  data,
  extraData,
}: any) {
  const [records, setRecords] = useState<any>({});
  function requestChart() {
    const { timeline_of_time } = data || {};
    const { timeline_of_start_time, timeline_of_end_time } =
      timeline_of_time || {};
    const { metricsKey, isMinutes, showTotal } = extraData;
    const { start_time, end_time, date_type } = filter;
    const dateType = dateTypeMap[date_type];
    const format = dateType === 'hour' ? 'YYYY-MM-DD HH:mm:ss' : 'YYYY-MM-DD';
    const timeList = isMinutes
      ? getTimeArray(filter)
      : getTimeSpaceArray(start_time, end_time, dateType, format);
    const fixedKeyList = metricsKey.map((val) => val.value);
    let timeline = dealWithOldTimeline(data, timeList, metricsKey);
    const chartData = DBO_REQ.moreKeys({
      timeline,
      timeline_of_start_time,
      timeline_of_end_time,
      filter,
      timeList,
      fixedKeyList,
      zeroIsNull: true,
      sortFunc: (x, y) => {
        return fixedKeyList.indexOf(x) - fixedKeyList.indexOf(y);
      },
    });

    if (showTotal) {
      timeline = timeline
        .map((val) => {
          const keys = Object.keys(val);
          let total = 0;
          keys.forEach((key) => {
            if (typeof val[key] === 'number') {
              total += val[key];
            }
          });
          if (total) {
            return {
              ...val,
              total,
            };
          }
          return undefined;
        })
        .filter(Boolean);
    }

    setRecords({
      ...chartData,
      tableData: timeline.sort(sortTimeListFunc('time', 'desc')),
    });
  }

  useEffect(() => {
    requestChart();
  }, [data]);

  return { data: records, refetch: requestChart };
}
export function useRealtimeRisingKeyword({
  filter,
  formatData,
  extraData,
}: {
  filter: any;
  formatData?: any;
  extraData?: any;
}) {
  const history = useHistory();
  const [records, setRecords] = useState<{
    list: Array<any>;
    new_start_date?: string;
    new_end_date?: string;
    old_start_date?: string;
    old_end_date?: string;
  }>({ list: [] });
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => setRecords({ list: [] }),
  });
  const { refresh, refreshTime } = useContext(AppContext);
  function requestKeyword(noLoading = false) {
    if (!filter?.edition_unified_id) return;
    setError(false);
    const urlParams =
      Object.keys(extraData || {}).length !== 0 ? objToUrl(extraData) : '';
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeRisingKeyword(filter, cancelFunc, urlParams, refreshTime)
          .then((data: any) => {
            const { bad, medium } = sentiment_frontier;
            const formatRecords = formatData?.(data, filter) || data;

            const {
              list,
              new_start_date,
              new_end_date,
              old_start_date,
              old_end_date,
            } = formatRecords;
            formatRecords.list = list.map((val) => {
              let type = '';
              let sentiment = 3;
              const { sentiment_rating, count, old_count } = val;
              if (sentiment_rating < bad) {
                type = '--negative';
                sentiment = 1;
              }
              if (sentiment_rating > medium) {
                type = '--positive';
                sentiment = 5;
              }
              const ratio = getRationPercentValue(count, old_count);
              return {
                ...val,
                type,
                newTime: [new_start_date, new_end_date],
                oldTime: [old_start_date, old_end_date],
                ratio,
                sentiment,
              };
            });

            setRecords(formatRecords);
          })
          .catch(() => {
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    requestKeyword();
  }, [filter]);

  useUpdateEffect(() => {
    requestKeyword(true);
  }, [refresh]);

  const { formatDate, text } = useMemo(() => {
    if (!filter) {
      return {
        formatDate: formatDateFunc('daily'),
        text: '',
      };
    }

    const { text } = getFilterDateQOQText(filter);
    let dateType = filter.date_type;
    if (
      filter.date_type === 'daily' &&
      moment(filter.start_time).isSame(moment(), 'day') &&
      moment(filter.end_time).isSame(moment(), 'day')
    ) {
      dateType = 'hourly';
    }

    return {
      formatDate: formatDateFunc(dateType),
      text,
    };
  }, [filter]);

  const keywordClick = (item: any) => {
    const { child, newTime } = item || [];
    const keywordInfo = child?.map((info: any) => info.keyword);
    const params = {
      ...filter,
      statistics_field: 'keyword',
      keyword: keywordInfo || [],
      topic: [],
    };

    if (newTime && newTime.length === 2) {
      params.start_time = newTime?.[0] || filter.start_time;
      params.end_time = newTime?.[1] || filter.end_time;
    }

    // console.log(params, "*-----------", item)
    goStatsDetailAction({
      params: {
        filter: params,
        statsDetailType: 'RealTimeKeyMetrics-keyword',
      },
      history,
    });
  };

  return {
    data: records,
    loading,
    error,
    refetch: requestKeyword,
    formatDate,
    keywordClick,
    text,
  };
}

export function useRealtimeSteamStats({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [timeline, setTimeline] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => {
      // setRecords({});
      setTimeline({});
    },
  });
  const { refresh, refreshTime } = useContext(AppContext);
  function requestStats(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeSteamStatsAndTimeline(filter, cancelFunc, refreshTime)
          .then((data: any) => {
            const { stats, timeline } = data;
            const {
              from_release_pc_console_stats,
              life_time_pc_console_stats,
              start_time,
              end_time,
              hour_link_of_time,
            } = stats;
            const steamData =
              life_time_pc_console_stats.find(
                (val) => val.channel === 'steam',
              ) || {};
            const databrainData =
              from_release_pc_console_stats.find(
                (val) => val.channel === 'steam',
              ) || {};
            const { list = [], old_list = [] } = steamData || {};

            const { list: db_list = [], old_list: db_old_list = [] } =
              databrainData || {};

            const formatList = dealwithSteamSelectedData([...list, ...db_list]);
            const formatOldList = dealwithSteamSelectedData([
              ...old_list,
              ...db_old_list,
            ]);

            setRecords({
              ...databrainData,
              ...steamData,
              hour_link_of_time,
              start_time,
              end_time,
              list: arrayToObj(formatList || [], 'key'),
              old_list: arrayToObj(formatOldList || [], 'key'),
            });
            setTimeline(timeline);
          })
          .catch((_err: any) => {
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    requestStats();
  }, [filter]);

  useUpdateEffect(() => {
    requestStats(true);
  }, [refresh]);

  return { data: records, timeline, loading, error, refetch: requestStats };
}

export function useRealtimeSteamAddedReviews({ data, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  function requestChart() {
    if (Object.keys(data).length === 0) return;
    const { config } = extraData;
    const result: {
      [key: string]: any;
    } = {};

    Object.entries(config).forEach(([key, { source }]) => {
      const sourceData = data?.[source] || {};
      const { list } = sourceData;
      const found =
        list.find((val: { channel: string }) => val.channel === 'steam') || {};
      let { timeline, old_time_line, channel } = found || {};

      if (source === 'from_release_pc_console_timeline') {
        timeline =
          timeline?.map((val) => {
            return {
              ...val,
              key: val.key.replace('_from_release', ''),
            };
          }) || [];

        old_time_line =
          old_time_line?.map((val) => {
            return {
              ...val,
              key: val.key.replace('_from_release', ''),
            };
          }) || [];
      }

      result[key] = {
        ...sourceData,
        channel,
        list: getTimeline(timeline || []),
        old_list: getTimeline(old_time_line || []),
      };
    });

    setRecords(result);
  }

  useEffect(() => {
    requestChart();
  }, [data]);

  return { data: records, refetch: requestChart };
}

export function useRealtimeCommunityStats({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [chartData, setChartData] = useState({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => setChartData({}),
  });
  const { refresh, refreshTime } = useContext(AppContext);
  function requestStats(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeCommunityStatsAndTimeline(filter, cancelFunc, refreshTime)
          .then((data: any) => {
            const { stats, timeline } = data;
            const { metricsKey } = extraData;
            const {
              new_start_date,
              new_end_date,
              old_start_date,
              old_end_date,
            } = stats;
            const topList = dealWithListObjData(stats, metricsKey);
            setRecords({
              ...stats,
              ...topList,
              oldTime: [old_start_date, old_end_date],
              time: [new_start_date, new_end_date],
            });
            setChartData(timeline);
          })
          .catch((_err: any) => {
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    requestStats();
  }, [filter]);

  useUpdateEffect(() => {
    requestStats(true);
  }, [refresh]);

  return { data: records, chartData, loading, error, refetch: requestStats };
}

export function useRealtimeStreamCommunityStats({ filter, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook({
    clear: () => setRecords({}),
  });
  const { refresh, refreshTime } = useContext(AppContext);
  function requestStreamChart(noLoading = false) {
    if (!filter.edition_unified_id) return;
    setError(false);
    wrapperRequest(
      (cancelFunc) =>
        getRealtimeChannelStream(filter, cancelFunc, refreshTime)
          .then((data: any) => {
            const {
              timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              link_of_end_time,
              link_of_start_time,
            } = data || {};
            const { metricsKey } = extraData;
            const { topList, colorList } = dealWithListData(data, metricsKey);
            const chartData = dealWithTimelineData({
              timeline_of_start_time,
              timeline_of_end_time,
              timeline: timeline_list,
              filter,
              colorList,
              metricsKey: metricsKey.map((val) => val.value),
            });

            setRecords({
              ...chartData,
              listData: {
                topList,
                time: [timeline_of_start_time, timeline_of_end_time],
                oldTime: [link_of_start_time, link_of_end_time],
              },
              tableData: timeline_list,
              timeline_of_start_time,
              timeline_of_end_time,
              link_of_end_time,
              link_of_start_time,
            });
          })
          .catch(() => {
            setError(true);
          }),
      noLoading,
    );
  }

  useEffect(() => {
    requestStreamChart();
  }, [filter]);

  useUpdateEffect(() => {
    requestStreamChart(true);
  }, [refresh]);

  return { data: records, loading, error, refetch: requestStreamChart };
}

export function useRealtimeCommunityTimeline({ filter, data, extraData }: any) {
  const [records, setRecords] = useState<any>({});
  function requestTimeline() {
    if (Object.keys(data).length === 0) return;
    const {
      timeline,
      timeline_of_start_time,
      timeline_of_end_time,
      list_stats,
    } = data || {};
    const chartData = DBO_REQ.moreKeys({
      timeline,
      timeline_of_start_time,
      timeline_of_end_time,
      filter,
      zeroIsNull: true,
      needTable: true,
    });
    const pieData = Object.entries(list_stats || {}).map(([name, value]) => ({
      name,
      value,
    }));
    const pieTotalData = pieData.reduce((sum, item) => sum + item.value, 0);
    setRecords({
      ...chartData,
      pieData: sortDataList(pieData, 'value'),
      pieTotalData,
    });
  }

  useEffect(() => {
    requestTimeline();
  }, [data]);

  return { data: records, refetch: requestTimeline };
}

export function useRealtimeRefresh(isToday: boolean) {
  const [refresh, setRefresh] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const duration = DBO_FWC.get('real_time_opinions_refresh_time');
  const [refreshTime, setRefreshTime] = useState(duration || 0);

  const startInterval = () => {
    if (refreshTime > 1) {
      intervalRef.current = setInterval(() => {
        setRefresh((prev) => !prev);
      }, refreshTime * 60 * 1000);
    }
  };

  const resetInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      startInterval();
    }
  };

  useEffect(() => {
    if (isToday) {
      startInterval();
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isToday, refreshTime]);

  return { refresh, setRefresh, resetInterval, refreshTime, setRefreshTime };
}
